import { Download, Fullscreen, FullscreenExit, HeightOutlined, Refresh, Search } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Autocomplete, Backdrop, Box, Button, Fade, Grid, IconButton, InputLabel, LinearProgress, Modal, TextField, Typography } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FieldTypes } from '../../component/CustomFormField/CustomFormField';
import { Accessibility, Charts, CustomFormField } from '../../component/component';
import { getActiveCampaignDetails, getReports, retailerSearch, skuSearch } from '../../service/Service';
import { AppContext } from '../../store/AppProvider';
import { SHOW_ERROR_ALERT, SHOW_INFO_ALERT } from '../../store/reducer/AlertNotifierReducer';
import { ErrorConstant, campaignList, countryOptions, localeList, regSources, stateByCountry } from '../../utils/Constants';
import { convertDate, FEATURES, isDate, isEmptyObj, scrollToElement, withoutTime } from '../../utils/Utils';

export const ViewReports = () => {
    const { setAlert, setShowLoader } = useContext(AppContext)
    const {
        handleSubmit,
        reset,
        control,
        watch,
        resetField,
        setError,
        formState: { errors }
    } = useForm({
        defaultValues: {
            register_date_from: null,
            register_date_to: null,
            source: [],
            product_sku: '',
            locale: '',
            optin: '',
            campaign_id: '',
            purchased_at: '',
            reportsBy: '',
            state: '',
            country: '',
        }
    })
    const [searchParams, setSearchParams] = useState({});

    const [isDownloading, setIsDownloading] = useState(false);
    const [loading, setLoading] = useState(false)

    const [productSearchResults, setProductSearchResults] = useState([]);

    const [campaignOptions, setCampaignOptions] = useState(campaignList);
    const [typingTimeout, setTypingTimeout] = useState(0);

    const [retailerSearchResults, setRetailerSearchResults] = useState([]);

    const [fullScreenChart, setFullScreenChart] = useState(false);
    const [chartData, setChartData] = useState(null)

    const selectedRegisterDateFrom = watch('register_date_from')

    useEffect(() => {
        const fetchCampaignList = async () => {
            try {
                setShowLoader(true)
                const data = await getActiveCampaignDetails()
                if (data?.length) {
                    const newCampaignList = [...data]?.map(ele => ele.campaign_id)
                    setCampaignOptions(newCampaignList)
                }
            } catch (error) {
                setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.fetchCampaignDetails })
            } finally {
                setShowLoader(false)
            }
        }
        fetchCampaignList()
    }, [setAlert, setShowLoader])

    const handleProductSearch = async (event) => {
        const searchText = event?.target?.value;
        // Clear the previous timeout
        clearTimeout(typingTimeout);

        // Set a new timeout
        const timeoutId = setTimeout(async () => {
            if (searchText.length > 2) {
                try {
                    const fetchedData = await skuSearch(searchText);
                    setProductSearchResults(fetchedData);
                } catch (error) {
                    setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.fetchProducts })
                }
            }
        }, 300); // Debounce time in milliseconds

        setTypingTimeout(timeoutId);
    };

    const handleRetailerSearch = async (event) => {
        const searchText = event.target.value;
        clearTimeout(typingTimeout);

        // Set a new timeout
        const timeoutId = setTimeout(async () => {
            if (searchText.length > 2) {
                try {
                    const fetchedData = await retailerSearch(searchText);
                    if (fetchedData?.length) setRetailerSearchResults(fetchedData);
                } catch (error) {
                    setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.fetchRetailers })
                }
            }
        }, 300); // Debounce time in milliseconds
        setTypingTimeout(timeoutId);
    }

    const exportAsCSV = async () => {
        try {
            setIsDownloading(true)
            const fetchedData = await getReports({ ...searchParams, isDownload: true })
            window.open(fetchedData, "_blank");
        } catch (error) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.downloadingCSV })
        } finally {
            setIsDownloading(false)
        }
    }

    const getStateObj = (stateCode) => {
        for (let item of selectedStateOptions) {
            if (item?.value === stateCode) return { state: (item?.label)?.toLowerCase(), stateCode }
        }
        return { state: '', stateCode: '' }
    }

    const notValidData = (formData) => {
        const {
            register_date_from,
            register_date_to } = formData
        let isNotValid = false;
        if (Boolean(register_date_from)) {
            if (!isDate(register_date_from)) {
                isNotValid = true
                setError('register_date_from', { type: 'custom', message: 'Wrong date format' })
            }
            if (!Boolean(register_date_to)) {
                isNotValid = true
                setError('register_date_to', { type: 'required', message: 'To date is required' })
            } else {
                if (isDate(register_date_to)) {
                    if ((withoutTime(register_date_to) < withoutTime(register_date_from))) {
                        setError('register_date_to', { type: 'custom', message: 'Register Date To cannot be past date' })
                        isNotValid = true
                    }
                } else {
                    isNotValid = true
                    setError('register_date_to', { type: 'custom', message: 'Wrong date format' })
                }
            }
        }
        return isNotValid;
    }

    const handleGenerateReport = async (formData) => {
        try {
            if (notValidData(formData)) {
                return
            }
            setLoading(true)
            setSearchParams(formData)
            const payload = {
                reportsBy: formData?.reportsBy,
                locale: formData?.locale,
                optin: formData?.optin,
                register_date_to: convertDate(formData?.register_date_to),
                register_date_from: convertDate(formData?.register_date_from),
                product_sku: formData?.product_sku,
                source: formData?.source || [],
                purchased_at: formData?.purchased_at,
                campaign_id: formData?.campaign_id === 'DEFAULT' ? '' : formData?.campaign_id,
                state: Boolean(formData?.state) ? getStateObj(formData?.state) : {},
                country: formData?.country,
            }
            const data = await getReports(payload)
            if (!isEmptyObj(data) && data?.data?.length) {
                setChartData({ ...data })
                scrollToElement('reports')
            } else {
                setChartData({})
                setAlert({ type: SHOW_INFO_ALERT, alertMessage: ErrorConstant.noRegisteredProductsFound })
            }
        } catch (error) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.error })
        } finally {
            setLoading(false)
        }
    };

    const onError = (formData) => { }

    const chartOptions = [
        { label: 'Reports By Registration Date', value: 'registrationByDate' },
        { label: 'Reports By Locale', value: 'reportsByLocale' },
        { label: 'Reports By Source', value: 'reportsBySource' },
        { label: 'Reports By Campaign', value: 'reportsByCampaign' },
        { label: 'Reports By Purchase At', value: 'reportsByPurchasedAt' },
        { label: 'Reports By SKU', value: 'reportsBySKU' },
    ]

    const selectedCountry = watch('country') || ""
    const selectedStateOptions = stateByCountry?.[selectedCountry] || [];

    return (
        <div>
            <div className='header-title' style={{ letterSpacing: '0.5px' }}>{('View Reports').toUpperCase()}</div>
            <form
                onSubmit={handleSubmit(handleGenerateReport, onError)}
                noValidate
                autoComplete="off">
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <InputLabel error={Boolean(errors?.register_date_to?.message || errors?.register_date_from?.message)} shrink style={{ fontSize: 20, fontWeight: 'bold' }}>Registered Between</InputLabel>
                        <Grid container>
                            <Grid item xs={12} md={5} >
                                <CustomFormField
                                    name="register_date_from"
                                    type={FieldTypes.date}
                                    rules={{ required: "From Date Required" }}
                                    errorMessage={errors?.register_date_from?.message}
                                    control={control} style={{ fontSize: 13 }} />
                            </Grid>
                            <Grid item xs={12} md={2} >
                                <HeightOutlined style={{ width: '100%', color: 'rgba(0, 0, 0,0.6)', transform: 'rotate(90deg)' }} fontSize='large' />
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <CustomFormField
                                    name="register_date_to"
                                    type={FieldTypes.date}
                                    rules={{ required: "To Date Required" }}
                                    minDate={selectedRegisterDateFrom}
                                    errorMessage={errors?.register_date_to?.message} control={control} style={{ fontSize: 13 }} />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Grid container>
                        <Grid item xs={12} md={5.5} >
                            <CustomFormField label='Source' name="source" type={FieldTypes.multiSelect} control={control} options={regSources} placeholder='source' />
                        </Grid>
                         <Grid item xs={12} md={1} />
                        <Grid item xs={12} md={5.5} >
                            <CustomFormField control={control} label='Email OptIn' name="optin" type={FieldTypes.select} options={['all', 'yes', 'no']} />
                        </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <InputLabel shrink htmlFor='product_sku' style={{ fontSize: 20, fontWeight: 'bold' }}>Product SKU</InputLabel>
                        <Controller
                            name="product_sku"
                            control={control}
                            render={({ field: { onChange, value: currVal } }) => (
                                <Autocomplete
                                    freeSolo
                                    disableClearable
                                    onInputChange={(event, newValue, reason) => {
                                        onChange(newValue);
                                        setProductSearchResults([])
                                    }}
                                    options={productSearchResults}
                                    sx={{
                                        "& .MuiInputBase-root": { height: "35px", backgroundColor: 'white' },
                                    }}
                                    getOptionLabel={(option) => option?.sku_name}
                                    inputValue={currVal}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            id="product_sku"
                                            InputLabelProps={{ shrink: true }}
                                            error={false}
                                            value={currVal || ''}
                                            onChange={handleProductSearch}
                                        />
                                    )}
                                />)}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Grid container>
                            <Grid item xs={12} md={5.5} >
                                <CustomFormField label='Country' type={FieldTypes.select} control={control} name={"country"} options={[...countryOptions]} onDropdownChange={() => resetField('state')} />
                            </Grid>
                            <Grid item xs={12} md={1} />
                            <Grid item xs={12} md={5.5} >
                                <CustomFormField label='State' type={FieldTypes.select} control={control} name={"state"} options={[...selectedStateOptions]} />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Grid container>
                            <Grid item xs={12} md={5.5} >
                                <CustomFormField control={control} label='Locale' name={"locale"} type={FieldTypes.select} options={localeList} />
                            </Grid>
                            <Grid item xs={12} md={1} />
                            <Grid item xs={12} md={5.5} >
                                <CustomFormField control={control} label='Campaign' name={"campaign_id"} type={FieldTypes.select} options={[...campaignOptions]} />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <InputLabel shrink htmlFor='product_sku' style={{ fontSize: 20, fontWeight: 'bold' }}>Purchased At</InputLabel>
                        <Controller
                            name="purchased_at"
                            control={control}
                            render={({ field: { onChange, value: currValue } }) =>
                            (
                                <Autocomplete
                                    freeSolo
                                    disableClearable
                                    options={retailerSearchResults}
                                    sx={{
                                        "& .MuiInputBase-root": { height: "35px", backgroundColor: 'white' },
                                    }}
                                    getOptionLabel={(option) => option?.retailer_name ?? ""}
                                    onInputChange={(event, newValue, reason) => {
                                        onChange(newValue);
                                        setRetailerSearchResults([])
                                    }}
                                    inputValue={currValue}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            id="purchased_at"
                                            InputLabelProps={{ shrink: true }}
                                            onChange={handleRetailerSearch}
                                            error={false}
                                            value={currValue}
                                        />)} />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <CustomFormField
                            rules={{ required: "Please choose a Report Type" }}
                            errorMessage={errors?.reportsBy?.message}
                            control={control} label='Report Type' name={"reportsBy"} type={FieldTypes.select} options={chartOptions} />
                    </Grid>
                    <Grid item xs={12} md={4} />
                    <Grid item xs={12} md={4}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 25 }}>
                            <LoadingButton
                                loading={loading}
                                loadingPosition="start"
                                startIcon={<Search />}
                                variant="contained"
                                type='submit' size="medium"
                                style={{ minWidth: '12%' }}
                            >
                                Generate
                            </LoadingButton>
                            <Button startIcon={<Refresh />} variant="outlined" size="medium" style={{ marginLeft: 20, minWidth: '12%', backgroundColor: 'white' }} onClick={() => reset()}>Reset</Button >
                        </div>
                    </Grid>
                </Grid>
            </form>
            <div style={{ marginTop: 50, }}>
                {loading && <LinearProgress />}
                {!isEmptyObj(chartData) &&
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', border: "2px solid #D9D9D9", borderRadius: 4 }}>
                        <Box id={`chart-container-${1}`} style={{ width: '100%', padding: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: 10 }}>
                                <Typography variant="h6"  > {chartData?.title} </Typography>
                                <div>
                                    <Accessibility feature={FEATURES.DOWNLOAD_REPORT_CSV}>
                                        <LoadingButton
                                            loading={isDownloading}
                                            loadingPosition="start"
                                            startIcon={<Download />}
                                            variant="outlined"
                                            onClick={() => exportAsCSV()}
                                        >
                                            Export CSV
                                        </LoadingButton>
                                    </Accessibility>
                                    <IconButton
                                        style={{ marginLeft: 10 }}
                                        onClick={() => setFullScreenChart(true)}
                                    >
                                        <Fullscreen />
                                    </IconButton>
                                </div>
                            </div>
                            {!isEmptyObj(chartData)
                                ? <div id="reports">
                                    <Charts dataset={chartData} />
                                </div>
                                : <div />}
                        </Box>
                        <Modal
                            open={!!fullScreenChart}
                            onClose={() => setFullScreenChart(false)}
                            closeAfterTransition
                            BackdropComponent={Backdrop}
                            BackdropProps={{
                                timeout: 500,
                            }}
                        >
                            <Fade in={!!fullScreenChart}>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '70vw',
                                        height: '65vh',
                                        backgroundColor: 'white',
                                        boxShadow: 24,
                                        p: 2,
                                    }}
                                >
                                    {!isEmptyObj(chartData) && (
                                        <>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="h6"  > {chartData?.title} </Typography>
                                                <div>
                                                    <Accessibility feature={FEATURES.DOWNLOAD_REPORT_CSV} >
                                                        <LoadingButton
                                                            loading={isDownloading}
                                                            loadingPosition="start"
                                                            startIcon={<Download />}
                                                            variant="contained"
                                                            onClick={() => exportAsCSV()}
                                                        >
                                                            Export CSV
                                                        </LoadingButton>
                                                    </Accessibility>
                                                    <IconButton
                                                        onClick={() => setFullScreenChart(false)}
                                                    >
                                                        <FullscreenExit />
                                                    </IconButton>
                                                </div>
                                            </div>
                                            {!isEmptyObj(chartData) ? <Charts dataset={chartData} /> : <div />}
                                        </>
                                    )}
                                </Box>
                            </Fade>
                        </Modal>
                    </div>}
            </div>
        </div>
    );
}
